import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiFetch, fmtDate, fmtMinutes, formatErr, STATUS_COLORS, PRIORITY_COLORS } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { generateOSPdf } from "@/lib/pdf";
import { ConcludeOSDialog } from "@/components/ConcludeOSDialog";
import { ArrowLeft, Bot, Paperclip, Send, Loader2, FileDown, Clock, Trash2, RotateCcw, Users as UsersIcon } from "lucide-react";

export default function OSDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isSupervisor = ["supervisor", "almoxarife"].includes(user?.role);
  const [os, setOs] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState("");
  const [concludeOpen, setConcludeOpen] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => api.get(`/os/${id}`).then((r) => setOs(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  const [technicians, setTechnicians] = useState([]);
  useEffect(() => {
    api.get("/users/technicians").then((r) => setTechnicians(r.data)).catch(() => {});
  }, []);

  if (!os) return <div className="text-muted-foreground">Carregando...</div>;

  const update = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/os/${id}`, patch);
      setOs(data);
      toast.success("OS atualizada");
      setNote("");
      return true;
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
      return false;
    } finally { setSaving(false); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const res = await apiFetch(`/os/${id}/attachments`, { method: "POST", body: fd });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success("Anexo enviado");
      load();
    } catch (err) {
      toast.error(err.message);
    }
    e.target.value = "";
  };

  const deleteOS = async () => {
    try {
      const { data } = await api.delete(`/os/${id}`);
      toast.success(`OS #${data.number} excluída`);
      nav("/os");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const askAI = async () => {    setAiLoading(true);
    setAiText("");
    try {
      const { data } = await api.post("/ai/suggest", {
        equipment_tag: os.equipment_tag,
        equipment_name: os.equipment_name,
        discipline: os.discipline,
        description: os.description,
      });
      setAiText(data.suggestion);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setAiLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="os-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => nav("/os")} className="rounded-sm" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Ordem de Serviço</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight font-mono-tag">
              OS #{os.number}
            </h1>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className={`rounded-sm text-sm ${STATUS_COLORS[os.status]}`}>{os.status}</Badge>
          <Badge variant="outline" className={`rounded-sm text-sm ${PRIORITY_COLORS[os.priority]}`}>{os.priority}</Badge>
          <Sheet open={aiOpen} onOpenChange={setAiOpen}>
            <SheetTrigger asChild>
              <Button onClick={() => { setAiOpen(true); if (!aiText) askAI(); }} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="ai-assist-btn">
                <Bot className="w-4 h-4" /> Assistente IA
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="font-heading flex items-center gap-2"><Bot className="w-5 h-5 text-orange-500" /> Recomendações IA</SheetTitle>
                <SheetDescription>
                  Análise técnica gerada por Claude Sonnet 4.6 para {os.equipment_name} ({os.equipment_tag})
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6">
                {aiLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Analisando problema...</div>
                ) : aiText ? (
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm leading-relaxed" data-testid="ai-response">{aiText}</div>
                ) : (
                  <div className="text-sm text-muted-foreground">Clique em "Gerar Análise" para receber sugestões.</div>
                )}
                <Button onClick={askAI} disabled={aiLoading} variant="outline" className="rounded-sm mt-6 w-full gap-2" data-testid="ai-regenerate">
                  <Bot className="w-4 h-4" /> {aiText ? "Gerar novamente" : "Gerar Análise"}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          <Button variant="outline" onClick={() => generateOSPdf(os)} className="rounded-sm gap-2" data-testid="export-pdf-btn"><FileDown className="w-4 h-4" /> PDF</Button>
          {isSupervisor && os.status === "Concluída" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="rounded-sm gap-2 text-yellow-700 dark:text-yellow-400 border-yellow-500/50" data-testid="reopen-os-btn">
                  <RotateCcw className="w-4 h-4" /> Reabrir OS
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reabrir OS #{os.number}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    <span className="block mb-2">O status voltará para <b>Em Andamento</b>. Informe o motivo — ele fica registrado no histórico:</span>
                    <textarea
                      value={reopenReason}
                      onChange={(e) => setReopenReason(e.target.value)}
                      placeholder="Ex: Vibração retornou no mancal 2 horas após a manutenção"
                      className="w-full mt-2 p-2 rounded-sm border text-sm bg-background"
                      rows={3}
                      data-testid="reopen-reason"
                    />
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => { update({ status: "Em Andamento", reopen_reason: reopenReason }); setReopenReason(""); }}
                    className="bg-yellow-600 hover:bg-yellow-700"
                    data-testid="confirm-reopen"
                  >
                    Reabrir OS
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {isSupervisor && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-sm gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-500/10"
                  data-testid="delete-os-btn"
                >
                  <Trash2 className="w-4 h-4" /> Excluir
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="delete-os-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir OS #{os.number}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    <span className="block mb-2">Esta OS será removida permanentemente do sistema, junto com seu histórico, anexos e notificações vinculadas.</span>
                    <span className="block text-xs font-semibold text-red-600">Esta ação não pode ser desfeita. Use apenas para OS abertas por engano.</span>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={deleteOS}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="confirm-delete-os"
                  >
                    Excluir OS
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Main info */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 rounded-sm shadow-none p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <Field label="Equipamento">{os.equipment_name} <span className="font-mono-tag text-orange-600 ml-1">[{os.equipment_tag}]</span></Field>
            <Field label="Setor">{os.sector}</Field>
            <Field label="Disciplina">{os.discipline}</Field>
            <Field label="Tipo">{os.maintenance_type}</Field>
            <Field label="Solicitante">{os.requester_name}</Field>
            <Field label="Técnico">
              {os.technician_names && os.technician_names.length > 0
                ? os.technician_names.join(", ")
                : os.technician_name || <span className="text-muted-foreground italic">Pendente</span>}
            </Field>
            <Field label="Abertura">{fmtDate(os.created_at)}</Field>
            <Field label="Fechamento">{os.closed_at ? fmtDate(os.closed_at) : <span className="text-muted-foreground italic">—</span>}</Field>
            <Field label="Tempo Parado">{fmtMinutes(os.downtime_minutes)}</Field>
          </div>

          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Descrição do Problema</Label>
            <p className="mt-1.5 text-sm bg-secondary rounded-sm p-3 whitespace-pre-wrap">{os.description}</p>
          </div>

          {os.recommended_actions && (
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Ações Recomendadas</Label>
              <p className="mt-1.5 text-sm bg-secondary rounded-sm p-3 whitespace-pre-wrap">{os.recommended_actions}</p>
            </div>
          )}
        </Card>

        {/* Update panel */}
        <Card className="rounded-sm shadow-none p-6 space-y-4">
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Atualizar Status</Label>
            <Select value={os.status} onValueChange={(v) => (v === "Concluída" ? setConcludeOpen(true) : update({ status: v }))}>
              <SelectTrigger className="rounded-sm mt-1.5" data-testid="update-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Aberta">Aberta</SelectItem>
                <SelectItem value="Em Andamento">Em Andamento</SelectItem>
                <SelectItem value="Concluída">Concluída</SelectItem>
                <SelectItem value="Cancelada">Cancelada</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Técnicos Atribuídos</Label>
            <div className="mt-1.5 space-y-1.5 border rounded-sm p-2 max-h-40 overflow-y-auto bg-background">
              {technicians.length === 0 && <div className="text-xs text-muted-foreground p-1">Nenhum técnico cadastrado</div>}
              {technicians.map((t) => {
                const checked = (os.technician_ids || []).includes(t.id) || os.technician_id === t.id;
                return (
                  <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-secondary p-1 rounded-sm">
                    <input
                      type="checkbox"
                      checked={checked}
                      data-testid={`tech-checkbox-${t.id}`}
                      onChange={(e) => {
                        const current = new Set(os.technician_ids && os.technician_ids.length ? os.technician_ids : (os.technician_id ? [os.technician_id] : []));
                        if (e.target.checked) current.add(t.id); else current.delete(t.id);
                        update({ technician_ids: Array.from(current) });
                      }}
                      className="w-4 h-4 rounded-sm accent-orange-500"
                    />
                    <span className="flex-1">{t.name}</span>
                    <span className="text-xs text-muted-foreground">{t.email}</span>
                  </label>
                );
              })}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">Marque um ou mais técnicos. Cada novo atribuído recebe notificação e email.</div>
          </div>

          <div>
            <Label>Ações Executadas</Label>
            <Textarea key={`ex-${os.updated_at}`} defaultValue={os.executed_actions} onBlur={(e) => e.target.value !== os.executed_actions && update({ executed_actions: e.target.value })} rows={3} className="rounded-sm mt-1.5" data-testid="update-executed" />
          </div>

          <div>
            <Label>Peças Utilizadas</Label>
            <Textarea key={`pu-${os.updated_at}`} defaultValue={os.parts_used} onBlur={(e) => e.target.value !== os.parts_used && update({ parts_used: e.target.value })} rows={2} className="rounded-sm mt-1.5" />
            {os.parts_items?.length > 0 && (
              <div className="mt-2 space-y-1" data-testid="os-parts-items">
                <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Baixadas do estoque</div>
                {os.parts_items.map((p, i) => (
                  <div key={i} className="text-xs flex justify-between bg-secondary rounded-sm px-2 py-1"><span><span className="font-mono-tag text-orange-600 mr-1">{p.code || "NÃO CADASTRADA"}</span>{p.name}</span><b>{p.quantity} {p.unit}</b></div>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label>Downtime (minutos)</Label>
            <Input key={`dt-${os.updated_at}`} type="number" defaultValue={os.downtime_minutes} onBlur={(e) => Number(e.target.value) !== os.downtime_minutes && update({ downtime_minutes: Number(e.target.value) })} className="rounded-sm mt-1.5" data-testid="update-downtime" />
          </div>

          <div className="pt-3 border-t space-y-2">
            <Label>Adicionar Comentário</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Observação..." className="rounded-sm" data-testid="note-input" />
            <Button onClick={() => note.trim() && update({ note })} disabled={!note.trim() || saving} className="w-full rounded-sm bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="add-note">
              <Send className="w-4 h-4" /> Registrar
            </Button>
          </div>

          <div className="pt-3 border-t">
            <input ref={fileRef} type="file" hidden onChange={upload} accept="image/*,application/pdf" />
            <Button onClick={() => fileRef.current?.click()} variant="outline" className="w-full rounded-sm gap-2" data-testid="upload-btn">
              <Paperclip className="w-4 h-4" /> Anexar Foto/Arquivo
            </Button>
          </div>
        </Card>
      </div>

      {/* Attachments */}
      {os.attachments?.length > 0 && (
        <Card className="rounded-sm shadow-none p-6">
          <h3 className="font-heading text-lg font-bold mb-4">Anexos ({os.attachments.length})</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {os.attachments.map((a) => (
              <a key={a.id} href={a.data_url} target="_blank" rel="noreferrer" className="block border rounded-sm overflow-hidden hover:border-orange-500 transition-colors">
                {a.content_type?.startsWith("image/") ? (
                  <img src={a.data_url} alt={a.filename} className="w-full h-32 object-cover" />
                ) : (
                  <div className="h-32 flex items-center justify-center bg-secondary text-xs">{a.content_type}</div>
                )}
                <div className="p-2 text-xs truncate">{a.filename}</div>
              </a>
            ))}
          </div>
        </Card>
      )}

      {/* Timeline */}
      <Card className="rounded-sm shadow-none p-6">
        <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-orange-500" /> Histórico</h3>
        <div className="space-y-3">
          {os.history?.map((h, i) => (
            <div key={i} className="flex gap-3 pb-3 border-b last:border-0 last:pb-0">
              <div className="w-2 h-2 rounded-full bg-orange-500 mt-2 shrink-0" />
              <div className="flex-1">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <div className="font-semibold text-sm">{h.action}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(h.at)}</div>
                </div>
                <div className="text-xs text-muted-foreground">Por {h.by}</div>
                {h.note && <div className="text-sm mt-1 bg-secondary rounded-sm p-2">{h.note}</div>}
              </div>
            </div>
          ))}
        </div>
      </Card>
      <ConcludeOSDialog
        os={os}
        open={concludeOpen}
        onOpenChange={setConcludeOpen}
        technicians={technicians}
        saving={saving}
        onConfirm={async (patch) => { if (await update(patch)) setConcludeOpen(false); }}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium">{children}</div>
    </div>
  );
}
